import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  combineLatest,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  catchError
} from 'rxjs';
import { DynamicCrmService } from '../../modules/services/dynamic-crm.service';
import * as dynamicCRMModels from '../../modules/models/dynamic-crm-models';
import { hasRichTextContent, sanitizeTrustedHtml } from '../../modules/shared/ckeditor/utils/rich-text.utils';

interface LabeledValueNumber {
  value: number;
  label: string;
}

interface LabeledValueString {
  value: string;
  label: string;
}

@Component({
  selector: 'app-trade-details-interactions',
  templateUrl: './trade-details-interactions.component.html',
  styleUrls: ['./trade-details-interactions.component.scss'],
  standalone: false
})
export class TradeDetailsInteractionsComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() partyDetails: { partyId: string; partyType: string; clientName?: string }[] = [];
  @Output() interactionsCountChange = new EventEmitter<number>();

  readonly interactionTypeOptions: LabeledValueNumber[] = [
    { value: 964110000, label: 'Meeting - Onsite' },
    { value: 964110001, label: 'Meeting - Offsite' },
    { value: 964110002, label: 'Meal / Social' },
    { value: 964110003, label: 'Phone Call' },
    { value: 964110004, label: 'Encounter' },
    { value: 964110005, label: 'Email' },
    { value: 804820000, label: 'Conference Call' },
    { value: 804820001, label: 'Conference / Event' },
    { value: 804820002, label: 'Finals Pitch' },
    { value: 412290001, label: 'Video Call' }
  ];

  readonly sentimentOptions: LabeledValueNumber[] = [
    { value: 804820000, label: 'Positive' },
    { value: 804820001, label: 'Negative' },
    { value: 804820002, label: 'Neutral' }
  ];

  readonly meetingPurposeOptions: LabeledValueString[] = [
    { value: '798960000', label: '6 Month Check-In' },
    { value: '798960001', label: 'Annual Review' },
    { value: '798960002', label: 'Annuity Review' },
    { value: '798960003', label: 'General Meeting' },
    { value: '798960004', label: 'Investment Strategy' },
    { value: '798960005', label: 'Online Portal' },
    { value: '798960006', label: 'Planning' },
    { value: '798960007', label: 'Proposal Discussion' },
    { value: '798960008', label: 'Quarterly Review' },
    { value: '798960009', label: 'Request for Cash' },
    { value: '798960010', label: 'Required Minimum Distribution' },
    { value: '798960011', label: 'Social Security' },
    { value: '798960012', label: 'Tax Inquiry' },
    { value: '798960013', label: 'Philanthropy' },
    { value: '798960014', label: 'Insurance' },
    { value: '798960015', label: 'Explicit Hold Recommendation - Brokerage' }
  ];

  readonly serviceTypeOptions: LabeledValueNumber[] = [
    { value: 798960004, label: 'Comprehensive Advisory Services (PA - Off Platform) - Quarterly' },
    { value: 412290001, label: 'Comprehensive Advisory Services (PA - Off Platform) - Annually' },
    { value: 798960005, label: 'Counseling Services (FOC) - Quarterly' },
    { value: 798960000, label: 'Financial Reporting (PA) - Monthly' },
    { value: 798960001, label: 'Financial Reporting (PA) - Quarterly' },
    { value: 798960002, label: 'Financial Reporting (FOS) - Monthly' },
    { value: 798960003, label: 'Financial Reporting (FOS) - Quarterly' }
  ];

  readonly reportTypeOptions: LabeledValueString[] = [
    { value: '798960000', label: 'Entity Level Balance Sheet' },
    { value: '798960001', label: 'Entity Level Income Statements' },
    { value: '798960002', label: 'Spending Analysis' },
    { value: '798960003', label: 'Consolidated Balance Sheet' },
    { value: '798960004', label: 'Consolidated Income Statement' },
    { value: '798960005', label: 'FOC Deliverable' }
  ];

  createInteractionForm = new FormGroup({
    interactionType: new FormControl<number | null>(null, Validators.required),
    interactionSubject: new FormControl<string>('', Validators.required),
    interactionDate: new FormControl<Date | null>(null, Validators.required),
    interactionMeetingPurpose: new FormControl<string[]>([]),
    interactionSentiment: new FormControl<number | null>(null),
    interactionCity: new FormControl<string>(''),
    interactionStateProvince: new FormControl<string>(''),
    interactionCountryRegion: new FormControl<string>(''),
    interactionDescription: new FormControl<string>(''),
    interactionServiceType: new FormControl<number | null>(null),
    interactionReportType: new FormControl<string[]>([]),
    interactionReportFromDate: new FormControl<Date | null>(null),
    interactionReportToDate: new FormControl<Date | null>(null)
  });

  readonly interactionsFetching$ = new BehaviorSubject<boolean>(false);
  readonly isDownloadingAttachment$ = new BehaviorSubject<boolean>(false);

  readonly interactionSortOrder$ = new BehaviorSubject<dynamicCRMModels.InteractionSortOrder>('desc');
  private readonly profileIds$ = new BehaviorSubject<string[]>([]);
  private hasResolvedProfiles = false;

  hasRequestedInteractions = false;
  private hasPendingPartyRefresh = false;
  isCreateInteractionOpen = false;
  isSubmittingInteraction = false;

  interactionsErrorMessage = '';
  createInteractionErrorMessage = '';
  createInteractionSuccessMessage = '';
  interactionAttachmentErrorMessage = '';

  selectedInteractionAttachmentFile: File | null = null;
  selectedInteractionAttachmentName = '';

  readonly interactionsFromCrm$: Observable<dynamicCRMModels.ICrmInteraction[]> = combineLatest([
    this.profileIds$,
    this.interactionSortOrder$
  ]).pipe(
    switchMap(([profileIds, sortOrder]) => {
      this.interactionsErrorMessage = '';
      if (!profileIds.length) {
        if (!this.hasResolvedProfiles) {
          return EMPTY;
        }
        return of([]);
      }

      this.interactionsFetching$.next(true);
      return this.dynamiccrmSvc.getInteractionsByProfiles({
        profileIds,
        sortOrder
      }).pipe(
        catchError(() => {
          this.interactionsErrorMessage = 'Unable to load interactions.';
          return of([]);
        }),
        finalize(() => this.interactionsFetching$.next(false))
      );
    }),
    shareReplay(1)
  );

  readonly interactions$: Observable<dynamicCRMModels.ICrmInteraction[]> = combineLatest([
    this.interactionsFromCrm$,
    this.interactionSortOrder$
  ]).pipe(
    map(([interactions, sortOrder]) => this.dynamiccrmSvc.sortInteractions(interactions, sortOrder)),
    tap(interactions => {
      this.renderedInteractions = interactions;
      this.emitInteractionsCount(interactions.length);
      this.scheduleInteractionOverflowDetection();
    }),
    shareReplay(1)
  );

  @ViewChildren('interactionBodyEl') interactionBodyEls?: QueryList<ElementRef<HTMLElement>>;
  private renderedInteractions: dynamicCRMModels.ICrmInteraction[] = [];

  constructor(
    private dynamiccrmSvc: DynamicCrmService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateReportTypeValidation(this.createInteractionForm.get('interactionServiceType')?.value ?? null);
    // Interactions load only when the interactions tab is clicked.
  }

  ngAfterViewInit(): void {
    this.interactionBodyEls?.changes.subscribe(() => this.scheduleInteractionOverflowDetection());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partyDetails']) {
      this.hasPendingPartyRefresh = true;

      if (!changes['partyDetails'].currentValue?.length) {
        this.profileIds$.next([]);
        this.hasRequestedInteractions = false;
        this.interactionsCountChange.emit(0);
      }

      if (changes['partyDetails'].currentValue?.length && this.hasRequestedInteractions) {
        this.getInteractions();
        this.hasPendingPartyRefresh = false;
      }
    }
  }

  getInteractions(): void {
    if (!this.partyDetails.length) {
      this.profileIds$.next([]);
      this.hasRequestedInteractions = false;
      this.interactionsCountChange.emit(0);
      return;
    }

    if (this.hasRequestedInteractions && !this.hasPendingPartyRefresh) {
      return;
    }

    this.interactionsErrorMessage = '';
    this.hasRequestedInteractions = true;
    this.hasPendingPartyRefresh = false;
    this.resolveSharedProfiles();
  }

  toggleInteractionSort(): void {
    const nextOrder: dynamicCRMModels.InteractionSortOrder = this.interactionSortOrder$.value === 'desc' ? 'asc' : 'desc';
    this.interactionSortOrder$.next(nextOrder);
  }

  toggleCreateInteraction(): void {
    this.isCreateInteractionOpen = !this.isCreateInteractionOpen;
    if (this.isCreateInteractionOpen) {
      this.createInteractionForm.patchValue({
        interactionType: null,
        interactionSubject: '',
        interactionDate: null,
        interactionMeetingPurpose: [],
        interactionSentiment: null,
        interactionCity: '',
        interactionStateProvince: '',
        interactionCountryRegion: '',
        interactionDescription: '',
        interactionServiceType: null,
        interactionReportType: [],
        interactionReportFromDate: null,
        interactionReportToDate: null
      });
    } else {
      this.resetCreateInteractionForm();
    }
  }

  openCreateInteraction(): void {
    if (!this.isCreateInteractionOpen) {
      this.toggleCreateInteraction();
    }
  }

  hasInteractionServiceTypeSelection(): boolean {
    return this.createInteractionForm.get('interactionServiceType')?.value != null;
  }

  onInteractionServiceTypeChange(value: number | null): void {
    this.updateReportTypeValidation(value);
  }

  resetCreateInteractionForm(): void {
    this.createInteractionForm.reset({
      interactionType: null,
      interactionSubject: '',
      interactionDate: null,
      interactionMeetingPurpose: [],
      interactionSentiment: null,
      interactionCity: '',
      interactionStateProvince: '',
      interactionCountryRegion: '',
      interactionDescription: '',
      interactionServiceType: null,
      interactionReportType: [],
      interactionReportFromDate: null,
      interactionReportToDate: null
    });
    this.updateReportTypeValidation(null);

    this.createInteractionErrorMessage = '';
    this.createInteractionSuccessMessage = '';
    this.selectedInteractionAttachmentFile = null;
    this.selectedInteractionAttachmentName = '';
  }

  getInteractionInitials(ownerName: string | undefined): string {
    if (!ownerName?.trim()) return '?';
    const parts = ownerName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getInteractionHeaderDate(interaction: dynamicCRMModels.ICrmInteraction): string {
    const source = interaction.modifiedOn || interaction.scheduledend;
    if (!source) return '';
    const d = new Date(source);
    if (isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${date} ${time}`;
  }

  getInteractionDateOnly(interaction: dynamicCRMModels.ICrmInteraction): string {
    if (!interaction.scheduledend) return '';
    const d = new Date(interaction.scheduledend);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  getInteractionServiceTypeLabel(interaction: dynamicCRMModels.ICrmInteraction): string {
    if (interaction.serviceTypeCode == null) return '';
    const match = this.serviceTypeOptions.find(o => o.value === Number(interaction.serviceTypeCode));
    return match?.label || String(interaction.serviceTypeCode);
  }

  getInteractionReportTypeLabel(interaction: dynamicCRMModels.ICrmInteraction): string {
    const raw = (interaction.reportTypeCodes || '').trim();
    if (!raw) return '';

    const labelByValue = new Map(this.reportTypeOptions.map(o => [o.value, o.label]));
    return raw
      .split(',')
      .map(code => code.trim())
      .filter(code => !!code)
      .map(code => labelByValue.get(code) || code)
      .join(', ');
  }

  getInteractionReportCoveredLabel(interaction: dynamicCRMModels.ICrmInteraction): string {
    const from = this.toReadableDate(interaction.reportCoveredFromDate);
    const to = this.toReadableDate(interaction.reportCoveredToDate);
    if (from && to) return `${from} - ${to}`;
    return from || to;
  }

  getInteractionMeetingPurposeLabel(interaction: dynamicCRMModels.ICrmInteraction): string {
    const raw = (interaction.meetingPurpose || '').trim();
    if (!raw) {
      return '';
    }

    const labelByValue = new Map(this.meetingPurposeOptions.map(o => [o.value, o.label]));
    return raw
      .split(',')
      .map(code => code.trim())
      .filter(code => !!code)
      .map(code => labelByValue.get(code) || code)
      .join(', ');
  }

  onInteractionAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    if (!file) {
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size === 0) {
      this.createInteractionErrorMessage = 'Attachment cannot be empty.';
      input.value = '';
      return;
    }
    if (file.size > maxSize) {
      this.createInteractionErrorMessage = 'Attachment must be 5 MB or less.';
      input.value = '';
      return;
    }

    this.selectedInteractionAttachmentFile = file;
    this.selectedInteractionAttachmentName = file.name;
    this.createInteractionErrorMessage = '';
  }

  removeInteractionAttachment(input?: HTMLInputElement): void {
    this.selectedInteractionAttachmentFile = null;
    this.selectedInteractionAttachmentName = '';
    if (input) {
      input.value = '';
    }
  }

  onInteractionDescriptionChanged(event: { html: string; editor: any }): void {
    this.createInteractionForm.get('interactionDescription')?.setValue(event.html);
    this.createInteractionForm.get('interactionDescription')?.markAsTouched();
  }

  sanitizeInteractionDescription(html: string | undefined): SafeHtml {
    if (!html) {
      return '';
    }
    return sanitizeTrustedHtml(this.sanitizer, html);
  }

  toggleInteractionExpand(interaction: dynamicCRMModels.ICrmInteraction): void {
    interaction.isExpanded = !interaction.isExpanded;
  }

  // Runs after layout so the clamped height is measurable; only shows "See more" when the text overflows.
  private scheduleInteractionOverflowDetection(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => this.detectInteractionOverflow()));
  }

  private detectInteractionOverflow(): void {
    if (!this.interactionBodyEls?.length) {
      return;
    }
    this.interactionBodyEls.forEach((ref, idx) => {
      const interaction = this.renderedInteractions[idx];
      if (!interaction || interaction.isExpanded) {
        return;
      }
      const el = ref.nativeElement;
      interaction.showSeeMore = el.scrollHeight - el.clientHeight > 1;
    });
    this.cdr.detectChanges();
  }

  hasInteractionDescription(interaction: dynamicCRMModels.ICrmInteraction): boolean {
    return hasRichTextContent(interaction.description || '');
  }

  submitInteraction(): void {
    if (this.createInteractionForm.invalid || this.isSubmittingInteraction) {
      this.createInteractionForm.markAllAsTouched();
      return;
    }

    const formValue = this.createInteractionForm.getRawValue();
    const selectedParties = this.partyDetails.filter(p => !!p.partyId);

    if (!selectedParties.length) {
      this.createInteractionErrorMessage = 'No client data is available to create this interaction.';
      return;
    }

    this.isSubmittingInteraction = true;
    this.createInteractionErrorMessage = '';
    this.createInteractionSuccessMessage = '';

    console.debug('[TradeDetailsInteractions] submitInteraction started', {
      selectedPartiesCount: selectedParties.length,
      hasAttachment: !!this.selectedInteractionAttachmentFile,
      attachmentName: this.selectedInteractionAttachmentFile?.name,
      attachmentSize: this.selectedInteractionAttachmentFile?.size,
      formValue
    });

    const interactionRequests = selectedParties.map(party =>
      this.dynamiccrmSvc.resolveCrmEntityByParty(party.partyId, party.partyType).pipe(
        first(),
        switchMap(resolved => {
          if (!resolved.crmId || !resolved.crmEntityType) {
            throw new Error('Could not resolve CRM contact/account for selected client.');
          }

          const payload: dynamicCRMModels.ICreateInteractionPayload = {
            aka_type: formValue.interactionType || undefined,
            subject: (formValue.interactionSubject || '').trim(),
            scheduledend: this.toUtcMidnightIso(formValue.interactionDate),
            description: (formValue.interactionDescription || '').trim() || undefined,
            ram_sentiment: formValue.interactionSentiment ?? undefined,
            rcm_meetingpurpose: (formValue.interactionMeetingPurpose || []).length
              ? (formValue.interactionMeetingPurpose || []).join(',')
              : undefined,
            rcm_city: (formValue.interactionCity || '').trim() || undefined,
            rcm_stateprovince: (formValue.interactionStateProvince || '').trim() || undefined,
            rcm_countryregion: (formValue.interactionCountryRegion || '').trim() || undefined,
            rcm_financialreporting: formValue.interactionServiceType ?? null,
            rcm_reporttype: formValue.interactionServiceType != null && (formValue.interactionReportType || []).length
              ? (formValue.interactionReportType || []).join(',')
              : null,
            rcm_reportcoveredfromdate: formValue.interactionServiceType != null
              ? this.toDateOnlyString(formValue.interactionReportFromDate)
              : null,
            rcm_reportcoveredtodate: formValue.interactionServiceType != null
              ? this.toDateOnlyString(formValue.interactionReportToDate)
              : null
          };

          console.debug('[TradeDetailsInteractions] submitInteraction payload prepared', {
            partyId: party.partyId,
            partyType: party.partyType,
            crmId: resolved.crmId,
            crmEntityType: resolved.crmEntityType,
            payload
          });

          if (resolved.crmEntityType === 'account') {
            payload['regardingobjectid_account_aka_meetingnote@odata.bind'] = `/accounts(${resolved.crmId})`;
          } else {
            payload['regardingobjectid_contact_aka_meetingnote@odata.bind'] = `/contacts(${resolved.crmId})`;
          }

          return this.dynamiccrmSvc.addInteractionToContact(payload, this.selectedInteractionAttachmentFile || undefined);
        })
      )
    );

    forkJoin(interactionRequests).pipe(first()).subscribe({
      next: responses => {
        console.debug('[TradeDetailsInteractions] submitInteraction success', {
          createdCount: responses?.length || 0,
          responses
        });
        this.isSubmittingInteraction = false;
        this.isCreateInteractionOpen = false;
        this.resetCreateInteractionForm();
        this.createInteractionSuccessMessage = 'Interaction(s) created successfully.';

        if (this.hasRequestedInteractions) {
          this.profileIds$.next([...this.profileIds$.value]);
        }
      },
      error: error => {
        console.error('[TradeDetailsInteractions] submitInteraction failed', {
          selectedParties,
          hasAttachment: !!this.selectedInteractionAttachmentFile,
          error
        });
        this.isSubmittingInteraction = false;
        this.createInteractionErrorMessage = 'Failed to create interaction. Please try again.';
      }
    });
  }

  downloadInteractionAttachment(interaction: dynamicCRMModels.ICrmInteraction): void {
    if (!interaction.activityid || this.isDownloadingAttachment$.value) {
      return;
    }

    this.interactionAttachmentErrorMessage = '';
    this.isDownloadingAttachment$.next(true);

    this.dynamiccrmSvc.downloadInteractionAttachment(interaction.activityid).pipe(first()).subscribe({
      next: bytes => {
        this.isDownloadingAttachment$.next(false);
        if (!bytes) {
          this.interactionAttachmentErrorMessage = 'Attachment is not available for download.';
          return;
        }

        const safeBytes = new Uint8Array(bytes.length);
        safeBytes.set(bytes);
        const blob = new Blob([safeBytes.buffer], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = interaction.attachmentName || 'interaction-attachment';
        anchor.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isDownloadingAttachment$.next(false);
        this.interactionAttachmentErrorMessage = 'Failed to download attachment. Please try again.';
      }
    });
  }

  private emitInteractionsCount(count: number): void {
    this.interactionsCountChange.emit(count);
  }

  private resolveSharedProfiles(): void {
    const partiesForLookup = this.partyDetails.filter(p => !!p.partyId);
    if (!partiesForLookup.length) {
      this.hasResolvedProfiles = true;
      this.profileIds$.next([]);
      return;
    }

    this.hasResolvedProfiles = false;
    const resolvers = partiesForLookup.map(p => this.dynamiccrmSvc.resolveCrmEntityByParty(p.partyId, p.partyType || '').pipe(first()));

    forkJoin(resolvers).subscribe({
      next: results => {
        this.hasResolvedProfiles = true;
        const profileIds = results
          .map(r => r.crmId)
          .filter((id): id is string => !!id);
        this.profileIds$.next(profileIds);
      },
      error: () => {
        this.hasResolvedProfiles = true;
        this.profileIds$.next([]);
        this.interactionsErrorMessage = 'Unable to resolve CRM profiles.';
      }
    });
  }

  private toUtcMidnightIso(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }

    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private toDateOnlyString(date: Date | null): string | null {
    if (!date) {
      return null;
    }

    const d = new Date(date);
    const year = d.getUTCFullYear();
    const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${d.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toReadableDate(dateValue: string | undefined): string {
    if (!dateValue) {
      return '';
    }

    const d = new Date(dateValue);
    if (isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  private updateReportTypeValidation(serviceTypeValue: number | null): void {
    const reportTypeCtrl = this.createInteractionForm.get('interactionReportType');
    if (!reportTypeCtrl) {
      return;
    }

    if (serviceTypeValue != null) {
      reportTypeCtrl.setValidators([Validators.required]);
    } else {
      reportTypeCtrl.clearValidators();
      this.createInteractionForm.patchValue({
        interactionReportType: [],
        interactionReportFromDate: null,
        interactionReportToDate: null
      }, { emitEvent: false });
    }

    reportTypeCtrl.updateValueAndValidity({ emitEvent: false });
  }
}
